/* Renders the wellness survey home page */

import React, { useEffect, useState } from 'react';
import { DetailsLayout } from '../utilities/layouts/DetailsLayout';
import { ContentLayout } from '../utilities/layouts/ContentLayout';
import { TextField, makeStyles, Typography } from '@material-ui/core';
import { connect, useDispatch } from 'react-redux';
import { retrieveFreeTextQuestionnaire, toggleFreeTextAlert } from '../../actions';
import { RequiresLogin } from '../utilities/commonUtilities/RequiresLogin';
import { PATH_HOME } from '../../containers/RoutePaths';
import { useCurrentVisit, useCurrentVisitAPI } from '../../userDataProviders/CurrentVisitProvider';
import { LoadingStatus, LoadingResult } from '../../utils/LoadingUtils';
import IsLoadingPage from '../IsLoadingPage';
import LoadingErrorPage from '../LoadingErrorPage';
import clsx from 'clsx';
import { PrimaryButton } from '../utilities/commonUtilities/GenericButtonElements';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Colors, COLOR_GREY } from '../utilities/MaterialWatchman';
import { styleBreakMobile, styleBreakSmallMobile, styleBreakDesktop } from '../utilities/layouts/Globals';
import DataSubmissionHandler from '../DataSubmissionHandler';
import { sendFreeTextQuestionnaireResponse, discardFreeTextAnswer, updateFreeTextAnswer } from '../../actions';
import { Redirect } from 'react-router-dom';
import AllTasksCompletedPage from '../AllTasksCompletedPage';
import * as TaskNames from './TaskNames';
import { taskCompleted } from '../../utils/VisitTaskHandler';
import AlertModalYesNo from '../utilities/AlertModal/AlertModalYesNo';
import { registerBeforeUnloadListener, unregisterBeforeUnloadListener } from './WellnessSurveyQuestion';

const useStyles = makeStyles(theme => ({
    freeTextSurveyContent: {
        display: 'block',
        [styleBreakDesktop(theme)]: {
            width: '684px',
        },
    },
    root: {
        width: '100%',
        marginTop: theme.spacing(3.75),
        '& .MuiOutlinedInput-root': {
            height: 'fit-content',
            '& fieldset': {
                borderRadius: '5px',
                border: '1px solid #A7B4BF',
                boxSizing: 'border-box',
            },
        },
        '& .MuiOutlinedInput-multiline': {
            padding: theme.spacing(2),
        },
        '& .MuiFormHelperText-contained': {
            textAlign: 'right',
            marginRight: theme.spacing(0),
            marginLeft: theme.spacing(0),
            marginTop: theme.spacing(1.25),
            color: theme.palette.primary.dark,
            fontWeight: '400',
            fontSize: '14px',
        },
        '& .MuiOutlinedInput-input': {
            fontWeight: '400',
            lineHeight: '150%',
            letterSpacing: '0.16px',
        },
    },
    input: {
        '&::placeholder': {
            color: COLOR_GREY.main,
            opacity: 1,
            fontWeight: '500',
            fontSize: '16px',
        },
    },
    rootLimitReached: {
        '& .MuiFormHelperText-contained': {
            color: theme.palette.error.main,
            fontWeight: '500',
        },
    },
    buttonCircularProgress: {},
    noSymptomsButton: {
        width: '100%',
        [styleBreakDesktop(theme)]: {
            width: '327px',
        },
    },
    noSymptomsButtonContained: {
        color: theme.palette.primary.main,
        background: Colors.COLOR_WHITE,
        border: '1px solid #7BCEF6',
        borderRadius: '5px',
        '&:disabled': {
            border: '1px solid #D0EDFB',
            borderRadius: '5px',
            background: Colors.COLOR_WHITE,
            color: theme.palette.primary.main,
            opacity: 0.8,
        },
        '&:hover': {
            border: `1px solid ${theme.palette.primary.dark}`,
            background: theme.palette.primary.dark,
            color: Colors.COLOR_WHITE,
        },
    },
    submitButton: {
        width: '100%',
        marginTop: theme.spacing(2),
        [styleBreakDesktop(theme)]: {
            marginTop: theme.spacing(0),
            marginLeft: theme.spacing(3.75),
            width: '327px',
        },
    },
    buttonBox: {
        '@media (max-width:359px)': {
            marginTop: theme.spacing(7.25),
            marginBottom: theme.spacing(3),
        },
        '@media (min-width:360px) and (max-width:374px)': {
            marginTop: theme.spacing(16.5),
            marginBottom: theme.spacing(3),
        },
        '@media (min-width:375px)': {
            left: theme.spacing(3),
            right: theme.spacing(3),
            bottom: theme.spacing(3),
            position: 'absolute',
            [styleBreakMobile(theme)]: {
                width: 'calc(100% - 48px)',
            },
            [styleBreakSmallMobile(theme)]: {
                width: 'calc(100% - 32px)',
            },
        },
        [styleBreakDesktop(theme)]: {
            display: 'flex',
            marginTop: theme.spacing(5),
            position: 'relative',
            left: theme.spacing(0),
            right: theme.spacing(0),
            bottom: theme.spacing(0),
        },
    },
    contentLayoutStyle: {
        paddingTop: theme.spacing(1),
        [styleBreakDesktop(theme)]: {
            paddingTop: theme.spacing(2),
        },
    },
    buttonLoading: {
        background: theme.palette.primary.dark,
    },
    noButtonAlert: {
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: '5px',
    },
    yesButtonAlert: {
        background: theme.palette.error.main,
        borderRadius: '5px',
    },
    noButtonAlertRoot: {
        '&:hover': {
            border: `1px solid ${theme.palette.primary.dark}`,
            background: theme.palette.primary.dark,
            color: Colors.COLOR_WHITE,
        },
    },
    yesButtonAlertRoot: {
        '&:hover': {
            background: theme.palette.error.main,
        },
    },
}));

const ButtonWithLoading = ({ isDisabled, isSubmitting, buttonText, onClick, dataCy, ...props }) => {
    const classes = useStyles();
    return (
        <PrimaryButton type="submit" disabled={isDisabled} {...props} onClick={onClick} data-cy={dataCy ? dataCy : ''}>
            {isSubmitting ? (
                <>
                    <CircularProgress className={classes.buttonCircularProgress} size={20} />
                </>
            ) : (
                buttonText
            )}
        </PrimaryButton>
    );
};

export const FreeTextSurvey = ({
    freeTextSurvey,
    fetchFreeTextSurveyQuestionnaire,
    isLoadingFreeTextSurveyQuestionnaire,
    freeTextSurveyQuestionnaireLoadingResult,
    isLoadingVisit,
    visitLoadingResult,
    stayOnFreeTextSurvey,
    exitFreeTextSurvey,
    history,
}) => {
    const classes = useStyles();
    const [answer, setAnswer] = useState(freeTextSurvey.question.answer);
    const currentVisit = useCurrentVisit();
    const { fetchCurrentVisit } = useCurrentVisitAPI();
    const CHARACTER_LIMIT = 250;
    const noSymptomsMessage = 'No Symptoms to Report';
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showExitPrompt, setShowExitPrompt] = useState(false);
    const [exitPath, setExitPath] = useState(PATH_HOME);
    const dispatch = useDispatch();

    const responseSubmissionFailureTitle = "We're having trouble submitting your responses";
    const responseSubmissionFailureBody =
        'Your computer might not be connected to the internet, or we may be experiencing problems saving your responses. Please try again.';

    const displayFreeTextSurveyAlert = freeTextSurvey.displayFreeTextSurveyAlert;

    const handleChange = e => {
        e.preventDefault();
        setAnswer(e.target.value);

        // If there is some user input data on the page, set show exit prompt
        setShowExitPrompt(e.target.value?.length !== 0);
    };

    const onButtonClick = e => {
        e.preventDefault();
        if (answer !== '') {
            dispatch(updateFreeTextAnswer(answer));
        }
        setIsSubmitting(true);
    };

    const freeTextSurveySubmissionSuccess = () => {
        // Discard free-text survey answer
        dispatch(discardFreeTextAnswer());
    };

    const BackClickHandler = () => {
        if (answer.length !== 0) {
            dispatch(toggleFreeTextAlert());
        } else {
            history.push(PATH_HOME);
        }
    };

    const onExitFreeTextSurveyQuestion = () => {
        history.push(exitPath);
        exitFreeTextSurvey();
    };

    const onStayFreeTextSurveyQuestion = () => {
        setExitPath(PATH_HOME);
        stayOnFreeTextSurvey();
    };

    useEffect(() => {
        if (!currentVisit.visit) {
            fetchCurrentVisit();
        }
    }, [fetchCurrentVisit, currentVisit]);

    useEffect(() => {
        if (!freeTextSurvey.freeTextQuestionnaireRetrieved) {
            fetchFreeTextSurveyQuestionnaire();
        }
    }, [fetchFreeTextSurveyQuestionnaire, freeTextSurvey.freeTextQuestionnaireRetrieved]);

    useEffect(() => {
        if (showExitPrompt) {
            registerBeforeUnloadListener();
            // Specify how to clean up after this effect:
            return function cleanUpShowExitPromptFunctionality() {
                // unregister the listener for beforeunload event
                unregisterBeforeUnloadListener();
                setShowExitPrompt(false);
            };
        }
    }, [showExitPrompt, setShowExitPrompt]);

    // If this questionnaire task is completed, but the visit is not completed, we bump the user to the home screen.
    if (currentVisit.isAllTasksRecentlyCompleted) {
        return <AllTasksCompletedPage />;
    } else if (taskCompleted(currentVisit.visit, TaskNames.FREE_TEXT_SURVEY) && !currentVisit.isAllTasksRecentlyCompleted) {
        return <Redirect to={PATH_HOME} />;
    } else if (isLoadingVisit || isLoadingFreeTextSurveyQuestionnaire) {
        return <IsLoadingPage />;
    } else {
        if (visitLoadingResult === LoadingResult.FAILED || freeTextSurveyQuestionnaireLoadingResult === LoadingResult.FAILED) {
            return <LoadingErrorPage />;
        } else {
            return (
                <>
                    <RequiresLogin>
                        <DetailsLayout
                            title={freeTextSurvey.question.title}
                            onClick={BackClickHandler}
                            dataCy={'text_header'}
                            showAlertModal={link => {
                                if (answer.length !== 0) {
                                    setExitPath(link);
                                    dispatch(toggleFreeTextAlert());
                                } else {
                                    history.push(link);
                                }
                            }}
                        >
                            <ContentLayout customStyles={classes.contentLayoutStyle}>
                                <div className={classes.freeTextSurveyContent}>
                                    <div>
                                        <div>
                                            <Typography variant="body1" data-cy={'text_instructions'}>
                                                {freeTextSurvey.question.text}
                                            </Typography>
                                        </div>
                                        <TextField
                                            inputProps={{
                                                maxLength: CHARACTER_LIMIT,
                                                'data-cy': 'input_response',
                                            }}
                                            InputProps={{
                                                classes: { input: classes.input },
                                            }}
                                            classes={{
                                                root: clsx(classes.root, {
                                                    [classes.rootLimitReached]: answer.length === CHARACTER_LIMIT,
                                                }),
                                            }}
                                            error={answer.length === CHARACTER_LIMIT}
                                            value={answer}
                                            helperText={`${answer.length === CHARACTER_LIMIT ? 'Character Limit Reached ' : ''}${
                                                answer.length
                                            }/${CHARACTER_LIMIT}`}
                                            onChange={handleChange}
                                            variant="outlined"
                                            multiline
                                            placeholder={'Enter your response'}
                                        />
                                    </div>
                                    <div className={classes.buttonBox}>
                                        <ButtonWithLoading
                                            isDisabled={answer.length !== 0}
                                            isSubmitting={isSubmitting && answer.length === 0}
                                            buttonText={noSymptomsMessage}
                                            classes={{ root: classes.noSymptomsButton, contained: classes.noSymptomsButtonContained }}
                                            onClick={onButtonClick}
                                            dataCy={'button_no_symptoms'}
                                        />
                                        <ButtonWithLoading
                                            isDisabled={answer.length === 0}
                                            isSubmitting={isSubmitting && answer.length !== 0}
                                            buttonText={'Submit Response to Doctor'}
                                            className={clsx(classes.submitButton, {
                                                [classes.buttonLoading]: isSubmitting && answer.length !== 0,
                                            })}
                                            onClick={onButtonClick}
                                            dataCy={'button_submit'}
                                        />
                                    </div>
                                </div>
                                <AlertModalYesNo
                                    isDisplayed={displayFreeTextSurveyAlert}
                                    alertModalTitle={'Delete your Response?'}
                                    alertModalBody={'If you leave now, your response will be deleted.'}
                                    onNoSelected={onStayFreeTextSurveyQuestion}
                                    onYesSelected={onExitFreeTextSurveyQuestion}
                                    onCloseSelected={onStayFreeTextSurveyQuestion}
                                    noButtonText={'Cancel'}
                                    yesButtonText={'Delete'}
                                    noButtonCustomStyle={classes.noButtonAlert}
                                    yesButtonCustomStyle={classes.yesButtonAlert}
                                    noButtonClasses={{ root: classes.noButtonAlertRoot }}
                                    yesButtonClasses={{ root: classes.yesButtonAlertRoot }}
                                    dataCyNoButton={'button_popup_cancel'}
                                    dataCyYesButton={'button_popup_delete'}
                                />
                            </ContentLayout>
                        </DetailsLayout>
                    </RequiresLogin>
                    <DataSubmissionHandler
                        setSubmitting={setIsSubmitting}
                        submitting={isSubmitting}
                        actionCreator={dispatchDataSubmissionResult => {
                            return sendFreeTextQuestionnaireResponse(answer !== '' ? answer : noSymptomsMessage, dispatchDataSubmissionResult, true);
                        }}
                        failureMsgTitle={responseSubmissionFailureTitle}
                        failureMsgBody={responseSubmissionFailureBody}
                        submissionSuccessHandler={freeTextSurveySubmissionSuccess}
                    />
                </>
            );
        }
    }
};

const mapStateToProps = state => {
    return {
        freeTextSurvey: state.freeTextSurvey,
        isLoadingFreeTextSurveyQuestionnaire: state?.dataLoading?.freeTextSurveyQuestionnaire?.loading !== LoadingStatus.ENDED,
        freeTextSurveyQuestionnaireLoadingResult: state?.dataLoading?.freeTextSurveyQuestionnaire?.loadingResult,
        isLoadingVisit: state?.dataLoading?.visit?.loading !== LoadingStatus.ENDED,
        visitLoadingResult: state?.dataLoading?.visit?.loadingResult,
    };
};

const mapDispatchToProps = dispatch => {
    const fetchFreeTextSurveyQuestionnaire = () => {
        dispatch(retrieveFreeTextQuestionnaire());
    };

    const stayOnFreeTextSurvey = () => {
        dispatch(toggleFreeTextAlert());
    };

    const exitFreeTextSurvey = () => {
        dispatch(toggleFreeTextAlert());
        dispatch(discardFreeTextAnswer());
    };

    return {
        fetchFreeTextSurveyQuestionnaire,
        stayOnFreeTextSurvey,
        exitFreeTextSurvey,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FreeTextSurvey);
